import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Clock, ChevronDown } from 'lucide-react';
import { apiService } from '../services/api';

const TestRunButton = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [testSuite, setTestSuite] = useState('all');
  const [customSpec, setCustomSpec] = useState('');
  const [grep, setGrep] = useState('');
  const [browser, setBrowser] = useState('chrome');
  const [headed, setHeaded] = useState(false);
  const navigate = useNavigate();

  const triggerTestRun = async () => {
    try {
      setIsLoading(true);
      
      // Build payload for targeted runs
      const payload = {
        browser,
        test_suite: testSuite,
        grep: grep.trim() || undefined,
        headed,
        custom_spec: testSuite === 'custom' ? (customSpec.trim() || undefined) : undefined,
      };

      // Trigger the test run
      const response = await apiService.triggerTestRun(Object.keys(payload).length ? payload : undefined);
      const newTaskId = response.data.task_id;
      
      // Navigate to test runner page with task ID
      navigate(`/test-runner?taskId=${newTaskId}`);
      
    } catch (err) {
      setIsLoading(false);
      alert('Failed to trigger test run: ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="relative inline-block text-left">
      <div className="flex space-x-2">
        <button
          onClick={triggerTestRun}
          disabled={isLoading}
          className={`px-4 py-2 rounded-md text-sm font-medium inline-flex items-center whitespace-nowrap transition-colors ${
            isLoading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-primary-600 hover:bg-primary-700'
          } text-white`}
        >
          {isLoading ? (
            <Clock className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          {isLoading ? 'Starting...' : 'Run Tests'}
        </button>
        <button
          type="button"
          onClick={() => setShowOptions((s) => !s)}
          className="px-2 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
          aria-haspopup="menu"
          aria-expanded={showOptions}
          title="Run specific tests"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {showOptions && (
        <div className="origin-top-right absolute right-0 mt-2 w-80 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 p-4 z-10">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Test suite</label>
              <select
                className="block w-full border-gray-300 rounded-md text-sm"
                value={testSuite}
                onChange={(e) => setTestSuite(e.target.value)}
              >
                <option value="all">All tests</option>
                <option value="homepage">Homepage</option>
                <option value="collections">Collections</option>
                <option value="product">Product</option>
                <option value="top-navigation">Top Navigation</option>
                <option value="bottom-navigation">Bottom Navigation</option>
                <option value="used-gear-homepage">Used Gear - Homepage</option>
                <option value="used-gear-flows">Used Gear - Flows</option>
                <option value="used-gear-seo">Used Gear - SEO</option>
                <option value="used-gear-collections">Used Gear - Collections</option>
                <option value="used-gear-product">Used Gear - Product</option>
                <option value="used-gear-bottom-navigation">Used Gear - Bottom Nav</option>
                <option value="used-gear-profile">Used Gear - Profile</option>
                <option value="custom">Custom path…</option>
              </select>
            </div>
            {testSuite === 'custom' && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Custom spec path</label>
                <input
                  type="text"
                  placeholder="e.g. specs/my-spec.spec.js"
                  className="block w-full border-gray-300 rounded-md text-sm px-2 py-1"
                  value={customSpec}
                  onChange={(e) => setCustomSpec(e.target.value)}
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Grep by title (optional)</label>
              <input
                type="text"
                placeholder="e.g. should load homepage"
                className="block w-full border-gray-300 rounded-md text-sm px-2 py-1"
                value={grep}
                onChange={(e) => setGrep(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Browser</label>
                <select
                  className="block w-full border-gray-300 rounded-md text-sm"
                  value={browser}
                  onChange={(e) => setBrowser(e.target.value)}
                >
                  <option value="chrome">chrome</option>
                  <option value="firefox">firefox</option>
                  <option value="safari">safari</option>
                  <option value="mobile-chrome">mobile chrome</option>
                  <option value="mobile-safari">mobile safari</option>
                </select>
              </div>
              <div className="flex items-end">
                <label className="inline-flex items-center text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={headed}
                    onChange={(e) => setHeaded(e.target.checked)}
                  />
                  Headed
                </label>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={triggerTestRun}
                className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
              >
                Run Selected
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestRunButton;
